import {useState, useEffect, useRef} from 'react';
import {type TabNode, Actions} from 'flexlayout-react';
import _ from 'lodash';
import {getContainer} from '../../utils/utils.js';

type MessageWithGroupId = {
  message: {groupId: string};
};

type MaybeMessageWithGroupId = {
  message?: {groupId?: string};
};

type LinkerGroupsConfig = {
  componentId: string;
  linkerGroups: string[];
};

function isMessageWithGroupId(message: MaybeMessageWithGroupId): message is MessageWithGroupId {
  return message?.message?.groupId !== undefined;
}

export default function useLinkerGroups(node: TabNode) {
  const linkerGroupsRef = useRef<string[]>([]);
  const [linkerGroups, setLinkGroups] = useState<string[]>([]);

  useEffect(() => {
    const {applicationClient} = getContainer();
    const {browserContextId} = applicationClient;

    const updateConfig = (newLinkerGroups: string[]) => {
      const myConfigObject = node.getConfig() as unknown as LinkerGroupsConfig;
      myConfigObject.linkerGroups = newLinkerGroups;
      setLinkGroups(newLinkerGroups);
      node.getModel().doAction(Actions.updateNodeAttributes(node.getId(), {config: myConfigObject}));
    };

    const windowLinkRegistered = (message: MaybeMessageWithGroupId) => {
      if (isMessageWithGroupId(message) && !linkerGroupsRef.current.includes(message.message.groupId)) {
        const newLinkerGroups: string[] = _.clone(linkerGroupsRef.current);
        newLinkerGroups.push(message.message.groupId);
        linkerGroupsRef.current = (newLinkerGroups);
        updateConfig(newLinkerGroups);
      }
    };

    const windowLinkUnregistered = (message: MaybeMessageWithGroupId) => {
      if (isMessageWithGroupId(message) && linkerGroupsRef.current.includes(message.message.groupId)) {
        let newLinkerGroups: string[] = _.clone(linkerGroupsRef.current);
        newLinkerGroups = newLinkerGroups.filter(groupId => groupId !== message.message.groupId);
        linkerGroupsRef.current = (newLinkerGroups);
        updateConfig(newLinkerGroups);
      }
    };

    const myConfigObject = node.getConfig() as unknown as LinkerGroupsConfig;
    if (myConfigObject?.linkerGroups && Array.isArray(myConfigObject.linkerGroups) && myConfigObject?.linkerGroups?.length) {
      for (const groupId of myConfigObject.linkerGroups) {
        void getContainer().linker.registerLink(groupId, node.getId(), myConfigObject.componentId);
      }

      linkerGroupsRef.current = (myConfigObject.linkerGroups);
      setLinkGroups(myConfigObject.linkerGroups);
    }

    getContainer().applicationClient.subscribe(`linker-${browserContextId}-${node.getId()}-registerLink`, windowLinkRegistered);
    getContainer().applicationClient.subscribe(`linker-${browserContextId}-${node.getId()}-unregisterLink`, windowLinkUnregistered);

    return () => {
      getContainer().applicationClient.unSubscribe(`linker-${browserContextId}-${node.getId()}-registerLink`, windowLinkRegistered);
      getContainer().applicationClient.unSubscribe(`linker-${browserContextId}-${node.getId()}-unregisterLink`, windowLinkUnregistered);
    };
  }, []);
  return linkerGroups;
}
